import { Icon } from '@aibees/shared/components';
import { usePlanDetailContext } from '@aibees/shared/features/price-and-plan/components/plan-detail/context';
import usePriceAndPlanStore from '@aibees/shared/features/price-and-plan/store';
import { useMount } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { Card, CardContent, Stack, Typography } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import * as Styled from './styles';
import * as SX from './sx';
import type { DialogRightSideProps as Props } from './types';

export const DialogRightSide = memo(({ className }: Props) => {
  const { totalCredits, contactCredits, aiPersonalityCredits } =
    usePlanDetailContext();

  const [yearly, setYearly] = usePriceAndPlanStore((s) => [
    s.yearly,
    s.setYearly,
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (totalCredits) {
      setLoading(true);
      setError(null);
      const timer = setTimeout(() => {
        setLoading(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [totalCredits]);

  useMount(() => {
    setYearly(false);
  });

  return (
    <Styled.Wrapper
      className={classnames('DialogRightSide', className)}
      sx={SX.root}
      variant="filled"
    >
      <CardContent padding="extra" sx={{ width: '100%' }}>
        {totalCredits ? (
          <Stack gap={5}>
            <Stack gap={1}>
              <Stack justifyContent="center" alignItems="center" gap={1}>
                <Typography variant="h5" fontWeight={700} color="grey.700">
                  {totalCredits}
                </Typography>
                <Typography variant="body4" fontWeight={500} color="grey.700">
                  Credits needed per Month
                </Typography>
              </Stack>
              <Card variant="outlined">
                <CardContent padding="normal" sx={SX.cardContent}>
                  <Typography variant="body3" fontWeight={500} color="grey.700">
                    Contact Credit
                  </Typography>
                  <Stack direction="row" alignItems="center" gap={1.5}>
                    <Typography
                      variant="body3"
                      fontWeight={500}
                      color="common.black"
                    >
                      {contactCredits}
                    </Typography>
                    <Icon name="coin" size={18} />
                  </Stack>
                </CardContent>
              </Card>
              <Card variant="outlined">
                <CardContent padding="normal" sx={SX.cardContent}>
                  <Typography variant="body3" fontWeight={500} color="grey.700">
                    AI Personality
                  </Typography>
                  <Stack direction="row" alignItems="center" gap={1.5}>
                    <Typography
                      variant="body3"
                      fontWeight={500}
                      color="common.black"
                    >
                      {aiPersonalityCredits}
                    </Typography>
                    <Icon name="coin" size={18} />
                  </Stack>
                </CardContent>
              </Card>
            </Stack>

            {/*<Box>
            <PlanCard
              plans={[]}
              yearly={yearly}
              selectedMonthlyCredits={totalCredits}
            />
          </Box>*/}
          </Stack>
        ) : (
          <Stack
            direction="column"
            justifyContent="center"
            alignItems="center"
            gap={1}
          >
            <Icon name="coin" size={22} color="grey.700" />
            <Typography
              variant="body3"
              fontWeight={400}
              textAlign="center"
              color="grey.700"
            >
              Fill in the details above, then hit "Calculate" to reveal your
              custom price estimate!
            </Typography>

            {error && (
              <Stack direction="row" gap={1}>
                <Icon name="info" color="error" />
                <Typography
                  variant="body4"
                  fontWeight={400}
                  textAlign="center"
                  color="error"
                >
                  {error}
                </Typography>
              </Stack>
            )}

            {loading && (
              <Stack direction="row" gap={1}>
                <Icon name="info" color="error" />
                <Typography
                  variant="body4"
                  fontWeight={400}
                  textAlign="center"
                  color="error"
                >
                  Calculating... Please wait.
                </Typography>
              </Stack>
            )}
          </Stack>
        )}
      </CardContent>
    </Styled.Wrapper>
  );
});

DialogRightSide.displayName = 'DialogRightSide';
